/*
 * File: AssignedRule.tsx
 * Project: beaker
 * -----
 * Last Modified: 27 Apr 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Dropdown, Menu, message } from 'antd';
import { LogoUpload } from 'components/molecules/LogoUpload';
import { UserForm } from 'components/molecules/UserForm';
import { Stack } from 'components/UI';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { FormikProvider, useFormik } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { EditProfile } from 'models/Users';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateUserMutation } from 'redux/services/cookieMonster/cookieMonsterApi';
import { setUser } from 'redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

const styles: InlineStylesModel = {
  //
};

export const EditProfilePage: React.FC = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const dispatch = useAppDispatch();
  const { user: reduxUser } = useAppSelector((state) => state.app);

  const profileId = reduxUser?.sub as string;

  /* ******************** API Hooks ******************** */

  const formik = useFormik<EditProfile>({
    enableReinitialize: true,
    initialValues: {
      email: reduxUser ? reduxUser.email : '',
      givenName: reduxUser ? reduxUser.given_name : '',
      familyName: reduxUser ? reduxUser.family_name : '',
      phoneNumber: reduxUser ? reduxUser['https://acuity.mdsiinc.com/user/user_metadata'].phone_number : ''
    },
    onSubmit: async (values) => {
      if (!reduxUser) {
        message.error("User doesn't exist");

        return;
      }
      try {
        const {
          given_name,
          user_metadata: { phone_number },
          family_name,
          email,
          name
        } = await updateUser({ userId: profileId, payload: { ...reduxUser, ...values } }).unwrap();

        dispatch(setUser({ ...reduxUser, ...{ given_name, family_name, phone_number: typeof phone_number === 'string' ? phone_number : undefined, email, name } }));
        message.success(`User ${name} was successfully updated!`);
      } catch (error) {
        message.error((error as { data: string }).data ?? 'User failed to be updated!');
      }
    }
  });

  /* ******************** Functions ******************** */

  const handleClose = (): void => {
    navigate('/');
  };

  const handleSave = async (close?: boolean): Promise<void> => {
    await formik.submitForm();

    if (close) {
      handleClose();
    }
  };
  const menuJSX = (
    <Menu disabledOverflow={true}>
      <Menu.Item key="1" onClick={(): Promise<void> => handleSave(false)}>
        Save
      </Menu.Item>
    </Menu>
  );

  return (
    <Stack flexDirection="column">
      <Col style={{ padding: 10, background: 'rgb(206, 213, 242)' }}>
        <Stack justifyContent="space-between">
          <AntPageTitle text={`Edit Profile`} />
          <Stack>
            <Button onClick={handleClose} icon={<ArrowLeftOutlined />}>
              Back
            </Button>
            <Dropdown.Button overlay={menuJSX} onClick={(): Promise<void> => handleSave(true)} loading={formik.isSubmitting} type="primary" icon={<SaveOutlined />}>
              Save and Exit
            </Dropdown.Button>
          </Stack>
        </Stack>
      </Col>
      <Card>
        <FormikProvider value={formik}>
          <UserForm />
        </FormikProvider>
      </Card>
      <Card>
        <LogoUpload profileData={reduxUser} />
      </Card>
    </Stack>
  );
};
